/**
 * B-spline / NURBS curves.
 *
 * A `spline` entity stores its control points (and optional weights) plus a degree, and is
 * evaluated as a **clamped uniform rational B-spline** — the same family as NURBS with an
 * identity knot vector, which is what mechanical CAD uses. Weights default to 1, so a plain
 * entity is an ordinary non-rational B-spline; a weight other than 1 pulls the curve toward
 * that control point (a full circle arc, a conic, …).
 *
 * Everything else in the kernel consumes curves through `entityPaths`, so flattening here is
 * what makes splines render, snap, hit-test, export and transform like any other entity.
 */

import { Vec2, clamp } from './math';
import type { SplineEntity } from './types';
import type { FlattenOptions } from './flatten';

const DEFAULT_SPLINE: FlattenOptions = { chordTolerance: 0.05 };

export interface SplineData {
  points: Vec2[];
  weights: number[];
  degree: number;
  knots: number[];
}

function knotsFor(count: number, degree: number): number[] {
  const inner = count - degree - 1;
  const knots: number[] = [];
  for (let i = 0; i <= degree; i++) knots.push(0);
  for (let i = 1; i <= inner; i++) knots.push(i / (inner + 1));
  for (let i = 0; i <= degree; i++) knots.push(1);
  return knots;
}

/** Control data with a closed spline's ends wrapped, so evaluation can stay uniform. */
export function splineData(entity: SplineEntity): SplineData {
  const degree = clamp(Math.round(entity.degree), 1, 3);
  const points = [...entity.controlPoints];
  const weights = points.map((_point, index) => entity.weights?.[index] ?? 1);

  if (entity.closed && points.length > degree) {
    for (let i = 0; i < degree; i++) {
      points.push(points[i]!);
      weights.push(weights[i]!);
    }
  }
  while (points.length < degree + 1) {
    points.push(points[points.length - 1] ?? Vec2.of(0, 0));
    weights.push(1);
  }

  return { points, weights, degree, knots: knotsFor(points.length, degree) };
}

function findSpan(count: number, degree: number, u: number, knots: readonly number[]): number {
  const last = count - 1;
  if (u >= knots[last + 1]!) return last;
  if (u <= knots[degree]!) return degree;
  let low = degree;
  let high = last + 1;
  let mid = Math.floor((low + high) / 2);
  while (u < knots[mid]! || u >= knots[mid + 1]!) {
    if (u < knots[mid]!) high = mid;
    else low = mid;
    mid = Math.floor((low + high) / 2);
  }
  return mid;
}

interface Homogeneous {
  x: number;
  y: number;
  w: number;
}

/** de Boor evaluation of the rational spline at `u` ∈ [0, 1]. */
export function splinePointAt(data: SplineData, u: number): Vec2 {
  const { points, weights, degree, knots } = data;
  if (points.length === 0) return Vec2.of(0, 0);
  if (points.length <= degree) return points[0]!;

  const t = clamp(u, 0, 1);
  const span = findSpan(points.length, degree, t, knots);
  const work: Homogeneous[] = [];
  for (let j = 0; j <= degree; j++) {
    const point = points[j + span - degree]!;
    const w = weights[j + span - degree]!;
    work.push({ x: point.x * w, y: point.y * w, w });
  }

  for (let r = 1; r <= degree; r++) {
    for (let j = degree; j >= r; j--) {
      const denominator = knots[j + 1 + span - r]! - knots[j + span - degree]!;
      const alpha = denominator === 0 ? 0 : (t - knots[j + span - degree]!) / denominator;
      const before = work[j - 1]!;
      const after = work[j]!;
      work[j] = {
        x: (1 - alpha) * before.x + alpha * after.x,
        y: (1 - alpha) * before.y + alpha * after.y,
        w: (1 - alpha) * before.w + alpha * after.w,
      };
    }
  }

  const result = work[degree]!;
  if (Math.abs(result.w) < 1e-12) return Vec2.of(result.x, result.y);
  return Vec2.of(result.x / result.w, result.y / result.w);
}

/** Points along the evaluated curve, spaced by a chord-tolerance guess. */
export function flattenSpline(
  entity: SplineEntity,
  opts: FlattenOptions = DEFAULT_SPLINE,
): Vec2[] {
  const data = splineData(entity);
  if (data.points.length === 0) return [];

  let length = 0;
  for (let index = 1; index < data.points.length; index++) {
    length += data.points[index - 1]!.distanceTo(data.points[index]!);
  }

  const tolerance = Math.max(opts.chordTolerance, 1e-6);
  const steps = clamp(Math.ceil(Math.sqrt(Math.max(length, 1e-6) / (8 * tolerance))) * 2, 24, 720);
  const out: Vec2[] = [];
  const last = entity.closed ? steps - 1 : steps;
  for (let step = 0; step <= last; step++) {
    out.push(splinePointAt(data, step / steps));
  }
  return out;
}

/** Direction of the curve at `u`, in radians — used for tangent references. */
export function splineTangentAt(entity: SplineEntity, u: number): number {
  const data = splineData(entity);
  const delta = 1e-4;
  const a = splinePointAt(data, clamp(u - delta, 0, 1));
  const b = splinePointAt(data, clamp(u + delta, 0, 1));
  const direction = b.sub(a);
  return direction.isZero(1e-12) ? 0 : direction.angle();
}
